import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import { castDraft } from "immer";
import { retailSlice } from "tsi-common-react/src/apps/retail/reducers";
import { RetailStore } from "tsi-common-react/src/models/location";
import {
    ProductUUID,
    isoProductUUID,
    isoSyncStoreID,
} from "tsi-common-react/src/models/nominals";

import { defaults } from "./defaults";
import {
    loadAllFlagshipStorePages,
    loadClosestStoresTempurpedic,
    loadFlagshipStorePage,
    loadStoreByExternalID,
} from "./thunks";

const tempurpedicRetailSlice = createSlice({
    name: "retail",
    initialState: defaults,
    reducers: {
        setSelectedProduct: (
            state,
            action: PayloadAction<ProductUUID | null>,
        ) => {
            state.selectedProduct = action.payload;
        },

        // This action is primarily for testing purposes
        setProductAvailability: (
            state,
            action: PayloadAction<{
                product: ProductUUID;
                stores: RetailStore[];
            }>,
        ) => {
            const key = isoProductUUID.unwrap(action.payload.product);
            state.productAvailability[key] = action.payload.stores.map(
                (s) => s.external_id,
            );
        },
    },
    extraReducers: (builder) => {
        builder.addCase(loadClosestStoresTempurpedic.pending, (state) => {
            state.isLoadingStores = true;
        });
        builder.addCase(
            loadClosestStoresTempurpedic.fulfilled,
            (state, action) => {
                state.stores = castDraft(action.payload.stores);
                if (action.payload.loadKey) {
                    state.storesLoadedKey = action.payload.loadKey;
                }
                // Always overwrite availability (even empty) to avoid stale cache
                if (action.meta.arg.selectedProduct) {
                    state.productAvailability[
                        isoProductUUID.unwrap(action.meta.arg.selectedProduct)
                    ] = action.payload.storesWithProduct.map(
                        (s) => s.external_id,
                    );
                }
                state.isLoadingStores = false;
            },
        );
        builder.addCase(loadClosestStoresTempurpedic.rejected, (state) => {
            state.isLoadingStores = false;
        });
        builder.addCase(loadStoreByExternalID.fulfilled, (state, action) => {
            // Add or update stores in the stores array
            action.payload.forEach((newStore) => {
                const existingIndex = state.stores.findIndex(
                    (s) => s.external_id === newStore.external_id,
                );
                if (existingIndex >= 0) {
                    state.stores[existingIndex] = castDraft(newStore);
                } else {
                    state.stores.push(castDraft(newStore));
                }
            });
        });
        builder.addCase(loadFlagshipStorePage.fulfilled, (state, action) => {
            const key = isoSyncStoreID.unwrap(action.payload.meta.sync_id);
            state.flagshipStorePages[key] = castDraft(action.payload);
        });
        builder.addCase(
            loadAllFlagshipStorePages.fulfilled,
            (state, action) => {
                action.payload.forEach((page) => {
                    const key = isoSyncStoreID.unwrap(page.meta.sync_id);
                    state.flagshipStorePages[key] = castDraft(page);
                });
            },
        );
        // Handle all base retail slice actions to keep state in sync
        builder.addMatcher(
            (action) => {
                // Only match base retail actions from the base slice
                // Exclude our own tempurpedic-specific actions
                return (
                    action.type.startsWith("retail/") &&
                    !action.type.startsWith(
                        "retail/loadClosestStoresTempurpedic",
                    ) &&
                    !action.type.startsWith("retail/loadStoreByExternalID") &&
                    !action.type.startsWith("retail/loadFlagshipStorePage") &&
                    !action.type.startsWith(
                        "retail/loadAllFlagshipStorePages",
                    ) &&
                    !action.type.startsWith("retail/setSelectedProduct") &&
                    !action.type.startsWith("retail/setProductAvailability")
                );
            },
            (state, action) => {
                // Apply base retail slice logic to the extended state
                // Use Object.assign to mutate the Immer draft directly
                const baseState = retailSlice.reducer(state, action);
                Object.assign(state, baseState);
            },
        );
    },
});

export const { setSelectedProduct, setProductAvailability } =
    tempurpedicRetailSlice.actions;

export const reducers = tempurpedicRetailSlice.reducer;
